import axios from 'axios'
import React, {useEffect, useState} from 'react'
import {Alert, Button, Form, Modal, Spinner} from 'react-bootstrap'
import {useParams} from 'react-router-dom'
import RichTextEditor from '../../../../components/RichTextEditor/RichTextEditor'
import apiRouterItems from '../../../../constants/apiRouterItems'
import {JobModel} from '../../../../models/JobModel'
import Select from 'react-select'
import {useSelector} from 'react-redux'
import {RootState} from '../../../../../setup'
import {ParentScaleModel} from '../../../../models/ParentScaleModel'
import useEventLog from '../../../../hooks/useEventLog'
import {EventCategories, EventTypes} from '../../../../constants/eventConstants'

export type JobEditModalMode = 'default' | 'readOnly' | 'descriptionOnly'

type ModalProps = {
  show: boolean
  job?: JobModel
  onHide: Function
  mode?: JobEditModalMode
}

const initData: JobModel = {
  code: '',
  name: '',
  altTitle: '',
  keyword: '',
  ONetCode: '',
  description: '',
  topCompetencyIds: [],
}

const JobEditModal: React.FC<ModalProps> = ({show, job, onHide, mode = 'default'}) => {
  const {id: assessmentId} = useParams<any>()
  const [processing, setProcessing] = useState(false)
  const [data, setData] = useState<JobModel | undefined>(job || initData)
  const [errors, setErrors] = useState('')
  const {addEventLog} = useEventLog()
  const isReadOnly = mode === 'readOnly'
  const isDescriptionOnly = mode === 'descriptionOnly'
  const updateData = (fieldsToUpdate: Partial<JobModel>): void => {
    const updatedData = Object.assign({...data}, fieldsToUpdate) as JobModel
    setData(updatedData)
  }
  const competencies = useSelector<RootState>(
    (state) => state.baseData.competencies
  ) as ParentScaleModel[]
  async function submitForm() {
    setProcessing(true)
    setErrors('')
    try {
      if (job) {
        const payload = isDescriptionOnly ? {description: data?.description} : data
        await axios.put(apiRouterItems.JOBS + job._id, payload)
        addEventLog({
          eventType: EventTypes.UPDATED,
          eventCategory: EventCategories.JOB,
          description: `Job '${data?.name}' was updated`,
        })
      } else {
        await axios.post(apiRouterItems.JOBS, {
          ...data,
          assessmentId: assessmentId,
        })
        addEventLog({
          eventType: EventTypes.CREATED,
          eventCategory: EventCategories.JOB,
          description: `Job '${data?.name}' was created`,
        })
      }
      setData(initData)
      onHide(true)
    } catch (err: any) {
      setErrors(`Could not ${job ? 'update' : 'create new'} item`)
      addEventLog({
        eventType: EventTypes.FAILURE,
        eventCategory: EventCategories.JOB,
        description: `Error: ${typeof err === 'string' ? err : JSON.stringify(err)}
        Description: ${job ? 'Update' : 'Create'} job
        Page: Super admin job page
        File: JobEditModal
        Action: axios.${job ? 'put' : 'post'}
      `,
      })
    }
    setProcessing(false)
  }
  useEffect(() => {
    if (show) {
      setData(job || initData)
    }
  }, [job, show])
  const topCompetencies = (data?.topCompetencyIds || []).map(
    (item) =>
      competencies.find((scale) => scale._id === item) || ({_id: '', label: ''} as ParentScaleModel)
  )
  const modalTitle =
    mode === 'readOnly' ? 'View Job' : mode === 'descriptionOnly' ? 'Edit Job Description' : 'Job'
  return (
    <Modal show={show} onHide={() => onHide()} size='lg'>
      <Modal.Header closeButton>
        <Modal.Title>{modalTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant='waring' show={!!errors}>
          {errors}
        </Alert>
        <Form>
          <Form.Group className='mb-3' controlId='code'>
            <div className='row'>
              <div className='col-3 d-flex align-items-center'>
                <Form.Label className='required'>Job Code</Form.Label>
              </div>
              <div className='col-9'>
                <Form.Control
                  type='text'
                  placeholder='Enter Job Code'
                  value={data?.code}
                  onChange={(e) => updateData({code: e.target.value})}
                  readOnly={isReadOnly || isDescriptionOnly}
                  disabled={isReadOnly || isDescriptionOnly}
                />
              </div>
            </div>
          </Form.Group>
          <Form.Group className='mb-3' controlId='ONetCode'>
            <div className='row'>
              <div className='col-3 d-flex align-items-center'>
                <Form.Label className='required'>ONet Code</Form.Label>
              </div>
              <div className='col-9'>
                <Form.Control
                  type='text'
                  placeholder='Enter ONet Code'
                  value={data?.ONetCode}
                  onChange={(e) => updateData({ONetCode: e.target.value})}
                  readOnly={isReadOnly || isDescriptionOnly}
                  disabled={isReadOnly || isDescriptionOnly}
                />
              </div>
            </div>
          </Form.Group>
          <Form.Group className='mb-3' controlId='name'>
            <div className='row'>
              <div className='col-3 d-flex align-items-center'>
                <Form.Label className='required'>Job Name</Form.Label>
              </div>
              <div className='col-9'>
                <Form.Control
                  type='text'
                  placeholder='Enter Job Name'
                  value={data?.name}
                  onChange={(e) => updateData({name: e.target.value})}
                  readOnly={isReadOnly || isDescriptionOnly}
                  disabled={isReadOnly || isDescriptionOnly}
                />
              </div>
            </div>
          </Form.Group>
          <Form.Group className='mb-3' controlId='altTitle'>
            <div className='row'>
              <div className='col-3 d-flex align-items-center'>
                <Form.Label>Alt Title</Form.Label>
              </div>
              <div className='col-9'>
                <Form.Control
                  type='text'
                  placeholder='Enter Alt Title'
                  value={data?.altTitle || ''}
                  onChange={(e) => updateData({altTitle: e.target.value})}
                  readOnly={isReadOnly || isDescriptionOnly}
                  disabled={isReadOnly || isDescriptionOnly}
                />
              </div>
            </div>
          </Form.Group>
          <Form.Group className='mb-3' controlId='keyword'>
            <div className='row'>
              <div className='col-3 d-flex align-items-center'>
                <Form.Label>Keyword</Form.Label>
              </div>
              <div className='col-9'>
                <Form.Control
                  type='text'
                  placeholder='Enter Keyword'
                  value={data?.keyword || ''}
                  onChange={(e) => updateData({keyword: e.target.value})}
                  readOnly={isReadOnly || isDescriptionOnly}
                  disabled={isReadOnly || isDescriptionOnly}
                />
              </div>
            </div>
          </Form.Group>

          <Form.Group className='mb-5' controlId='description'>
            <div className='row'>
              <div className='col-3'>
                <Form.Label className=''>Description</Form.Label>
              </div>
              <div className='col-9'>
                {isReadOnly ? (
                  <div
                    className='border rounded p-3 bg-light'
                    dangerouslySetInnerHTML={{__html: data?.description || ''}}
                  />
                ) : (
                  <RichTextEditor
                    height={200}
                    value={data?.description}
                    onChange={(value: string) => updateData({description: value})}
                  />
                )}
              </div>
            </div>
          </Form.Group>
          <Form.Group className='mb-5' controlId='description'>
            <div className='row'>
              <div className='col-3'>
                <Form.Label className=''>Attached Competencies</Form.Label>
              </div>
              <div className='col-9'>
                <Select
                  isMulti
                  closeMenuOnSelect={false}
                  name='Attached Competencies'
                  options={competencies}
                  getOptionLabel={(scale: ParentScaleModel) => scale.label}
                  getOptionValue={(scale: ParentScaleModel) => scale._id || ''}
                  value={topCompetencies}
                  onChange={(newValues) =>
                    updateData({topCompetencyIds: newValues.map((item) => item._id || '')})
                  }
                  isDisabled={isReadOnly || isDescriptionOnly}
                />
              </div>
            </div>
          </Form.Group>
        </Form>
      </Modal.Body>
      {!isReadOnly && (
        <Modal.Footer>
          <Button variant='secondary' onClick={() => onHide()}>
            Cancel
          </Button>
          <Button
            variant='primary'
            onClick={() => submitForm()}
            disabled={
              processing ||
              (isDescriptionOnly ? false : !data?.name || !data?.code || !data?.ONetCode)
            }
          >
            {processing && <Spinner animation='border' size='sm' />}
            {job ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  )
}

JobEditModal.defaultProps = {
  show: false,
  job: undefined,
  onHide: (reloadRequest: boolean) => {},
  mode: 'default',
}

export default JobEditModal
